// src/bonus/index.js — the staff-app bonus calculator (v130.20).
//
// Matt, 18 Aug: "they enter in their salary, it takes their position from
// shift and can tell them what bonus they are on track for for the month.
// they can also speculatively put the bonus in." Then, after seeing it:
//   • "the way the bonus has been described in the past is that its a value.
//      so if you are a store manager on 4500, its 450 and 450." — so the
//      headline is the two POTS in dirhams, not a pair of percentages.
//   • the name list must be that store's people only
//   • it must sit behind a PIN
//
// PRIVACY, deliberately: the salary NEVER leaves this screen. Not sent to the
// Worker, not written to localStorage, not kept when the tab changes — the
// till is a SHARED device, so a remembered salary would show the next person
// what the last one earns. The PIN gate is the other half of that: your bonus
// is nobody else's business, so the till asks who you are and proves it
// against the same rate-limited, lockout-aware /api/verify-pin the clock uses.
//
// Bare-name globals (ALL_STAFF, currentUser, DB, escapeHtml) are typeof-
// guarded — the classic-script bundle contract.

import { rateForRole, calcBonus, displayRate, bonusLadder, BONUS_THRESHOLD_VARIABLE } from '../shared/bonus-scheme.js';

var _perf = null;         // { pct, country, month, through } from the Worker
var _perfLoaded = false;
var _staffId = null;      // who the calculator is for
var _unlocked = false;    // has this person proved who they are?
var _pinEntry = '';
var _pinMsg = '';
var _whatIf = null;       // the speculative percentage, when one is set

function esc(s) {
  return (typeof escapeHtml === 'function') ? escapeHtml(s)
    : String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
}
function root() { return document.getElementById('bonusScreen'); }
function money(n) {
  return 'AED ' + (Math.round(Number(n) || 0)).toLocaleString('en-US');
}
function myStore() {
  return (typeof currentUser !== 'undefined' && currentUser && currentUser.store) ? currentUser.store : null;
}

// v130.20 — THIS STORE's people only. The picker used to list the whole
// company, which is both a long scroll and none of this till's business.
function roster() {
  var all = (typeof ALL_STAFF !== 'undefined' && Array.isArray(ALL_STAFF)) ? ALL_STAFF : [];
  var store = myStore();
  var here = all.filter(function (s) {
    if (!s || s.active === false) return false;
    if (!store) return true;                       // no store context: do not hide everyone
    return s.store === store || s.work_store === store;
  });
  return here.sort(function (a, b) { return String(a.name).localeCompare(String(b.name)); });
}

function selected() {
  var list = roster();
  for (var i = 0; i < list.length; i++) if (String(list[i].id) === String(_staffId)) return list[i];
  return null;
}

function activePct() {
  if (_whatIf != null) return _whatIf;
  return (_perf && _perf.pct != null) ? _perf.pct : null;
}

// ── the PIN gate ─────────────────────────────────────────────────────────────
export function pickStaff(id) {
  _staffId = id || null;
  _unlocked = false;
  _pinEntry = '';
  _pinMsg = '';
  _whatIf = null;
  render();
}
export function pinKey(d) {
  if (_pinEntry.length >= 6) return;
  _pinEntry += String(d);
  _pinMsg = '';
  render();
  if (_pinEntry.length === 6) submitPin();
}
export function pinBack() { _pinEntry = _pinEntry.slice(0, -1); _pinMsg = ''; render(); }
export function lock() { _unlocked = false; _pinEntry = ''; _pinMsg = ''; render(); }

async function submitPin() {
  var pin = _pinEntry;
  _pinEntry = '';
  _pinMsg = 'Checking…';
  render();
  try {
    // The SAME server check the clock uses: bcrypt, a per-IP rate limit and a
    // per-staff lockout. Nothing about the PIN is verified in the browser.
    var res = await DB.staff.verifyPin(_staffId, pin);
    if (res && res.valid) {
      _unlocked = true;
      _pinMsg = '';
      render();
      return;
    }
    if (res && res.locked && /on this connection/.test(res.reason || '')) {
      _pinMsg = res.reason;   // v133.35: too many wrong PINs on this connection (it says how long)
    } else if (res && res.locked) {
      _pinMsg = 'PIN locked for 15 minutes — please ask your manager';
    } else if (res && res.warning && typeof res.attemptsRemaining === 'number') {
      _pinMsg = res.attemptsRemaining === 1
        ? 'Incorrect PIN — 1 more try before it locks'
        : 'Incorrect PIN — ' + res.attemptsRemaining + ' tries left before it locks';
    } else {
      _pinMsg = 'Incorrect PIN — try again';
    }
  } catch (e) {
    _pinMsg = 'Could not check your PIN — try again';
  }
  render();
}

export function setWhatIf(v) {
  var n = parseFloat(v);
  _whatIf = isFinite(n) ? n : null;
  render(true);
}
export function clearWhatIf() { _whatIf = null; render(true); }
export function recalc() { render(true); }

function loadPerformance() {
  if (_perfLoaded) return Promise.resolve();
  _perfLoaded = true;
  var store = myStore();
  if (!store || typeof DB === 'undefined' || !DB.workerUrl) return Promise.resolve();
  return fetch(DB.workerUrl() + '/api/bonus/performance?store=' + encodeURIComponent(store), { cache: 'no-store' })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (d) { if (d && d.ok) _perf = d; })
    .catch(function () { /* the calculator still works with a what-if */ });
}

// ── the ladder — in MONEY once we know the salary ───────────────────────────
function ladderHtml(rate, current, salary) {
  var showMoney = salary > 0;
  var cell = function (r) { return showMoney ? money((salary * r) / 100) : displayRate(r) + '%'; };
  var rows = bonusLadder(rate).map(function (s) {
    var on = current != null && Math.round(current) === s.performance;
    var earns = s.totalRate > 0;
    return '<tr style="background:' + (on ? 'var(--elc-green-soft)' : (earns ? 'var(--card)' : 'var(--danger-bg)')) + '">' +
      '<td style="padding:7px 10px;font-weight:' + (on ? '800' : '600') + '">' + s.performance + '%</td>' +
      '<td style="padding:7px 10px;text-align:end">' + cell(s.variableRate) + '</td>' +
      '<td style="padding:7px 10px;text-align:end;color:var(--sub)">' + cell(s.fixedRate) + '</td>' +
      '<td style="padding:7px 10px;text-align:end;font-weight:800">' + cell(s.totalRate) + '</td>' +
    '</tr>';
  }).join('');
  return '<table style="width:100%;border-collapse:collapse;font-size:13px;border:1px solid var(--border);border-radius:8px;overflow:hidden">' +
    '<thead><tr style="background:#0B6B34;color:#fff">' +
      '<th style="padding:8px 10px;text-align:start">Performance</th>' +
      '<th style="padding:8px 10px;text-align:end">Variable</th>' +
      '<th style="padding:8px 10px;text-align:end">Fixed</th>' +
      '<th style="padding:8px 10px;text-align:end">Total</th>' +
    '</tr></thead><tbody>' + rows + '</tbody></table>' +
    '<div style="font-size:11.5px;color:var(--stores-ink-3);margin-top:6px">Below ' + BONUS_THRESHOLD_VARIABLE +
      '% of target there is no bonus. The fixed half only starts at 100%.</div>';
}

function card(inner) {
  return '<div style="max-width:620px;margin:0 auto;padding:14px">' +
    '<div style="background:var(--card);border:1px solid var(--border);border-radius:14px;padding:16px">' +
      '<div style="font-size:17px;font-weight:800;margin-bottom:2px">💰 Your bonus</div>' +
      '<div style="font-size:12.5px;color:var(--sub);margin-bottom:12px">March 2026 scheme &middot; work out what you are on track for</div>' +
      inner +
    '</div></div>';
}

function pickerHtml() {
  var list = roster();
  return '<label style="display:block;font-size:12.5px;font-weight:700;margin-bottom:4px">Who are you?</label>' +
    '<select onchange="Bonus.pickStaff(this.value)" style="width:100%;padding:11px;border:1px solid var(--border-strong);border-radius:10px;font-size:15px;background:var(--input-bg)">' +
      '<option value="">Choose your name…</option>' +
      list.map(function (s) {
        return '<option translate="no" value="' + s.id + '"' + (String(s.id) === String(_staffId) ? ' selected' : '') + '>' + esc(s.name) + '</option>';
      }).join('') +
    '</select>' +
    (myStore() ? '<div style="font-size:11.5px;color:var(--stores-ink-3);margin-top:5px">' + esc(myStore()) + '</div>' : '');
}

export function render(keepSalary) {
  var el = root();
  if (!el) return;
  var salaryVal = '';
  if (keepSalary) {
    var cur = document.getElementById('bonusSalary');
    if (cur) salaryVal = cur.value;
  }

  var me = selected();
  var picker = pickerHtml();

  if (!me) {
    el.innerHTML = card(picker +
      '<div style="font-size:13px;color:var(--sub);margin-top:10px">Choose your name, enter your PIN, and the calculator uses your role.</div>');
    return;
  }

  // The PIN gate — your bonus is nobody else's business on a shared till.
  if (!_unlocked) {
    var dots = '';
    for (var i = 0; i < 6; i++) {
      dots += '<span style="display:inline-block;width:13px;height:13px;border-radius:50%;margin:0 4px;background:' +
        (i < _pinEntry.length ? '#009E3D' : 'var(--border-strong)') + '"></span>';
    }
    var keys = [1, 2, 3, 4, 5, 6, 7, 8, 9].map(function (n) {
      return '<button onclick="Bonus.pinKey(' + n + ')" style="padding:16px 0;font-size:20px;font-weight:700;border:1px solid var(--border-strong);background:var(--card);border-radius:12px;cursor:pointer;font-family:inherit">' + n + '</button>';
    }).join('');
    el.innerHTML = card(picker +
      '<div style="margin-top:14px;text-align:center">' +
        '<div style="font-size:13.5px;font-weight:700;margin-bottom:4px">Enter your PIN</div>' +
        '<div style="font-size:12px;color:var(--sub);margin-bottom:10px">Your bonus is private — only you can open it.</div>' +
        '<div style="margin:10px 0 14px">' + dots + '</div>' +
        '<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;max-width:280px;margin:0 auto">' +
          keys +
          '<button onclick="Bonus.pinBack()" style="padding:16px 0;font-size:16px;border:1px solid var(--border-strong);background:var(--card);border-radius:12px;cursor:pointer;font-family:inherit">&#9003;</button>' +
          '<button onclick="Bonus.pinKey(0)" style="padding:16px 0;font-size:20px;font-weight:700;border:1px solid var(--border-strong);background:var(--card);border-radius:12px;cursor:pointer;font-family:inherit">0</button>' +
          '<span></span>' +
        '</div>' +
        (_pinMsg ? '<div style="font-size:13px;color:var(--danger-deep);margin-top:12px">' + esc(_pinMsg) + '</div>' : '') +
      '</div>');
    return;
  }

  var roleInfo = rateForRole(me.role);
  if (!(roleInfo.rate > 0)) {
    el.innerHTML = card(picker +
      '<div style="margin-top:12px;padding:12px;background:var(--warn-bg);border:1px solid var(--stores-warn-line);border-radius:10px;font-size:13.5px;color:var(--warn-deep)">' +
      'The March 2026 bonus scheme covers <b>sales assistants</b> and <b>store managers</b>. ' +
      'It does not cover ' + esc(roleInfo.label || 'this role') + ', so there is nothing to calculate here.</div>');
    return;
  }

  var pct = activePct();
  var salaryNum = parseFloat(String(salaryVal).replace(/[^0-9.]/g, ''));
  var b = (pct != null) ? calcBonus(roleInfo.rate, pct, salaryNum) : null;
  var potEach = salaryNum > 0 ? (salaryNum * roleInfo.rate) / 100 : null;   // the "450 and 450"

  var live = '';
  if (_perf && _perf.pct != null) {
    live = '<div style="font-size:12.5px;color:var(--sub);margin-top:6px">' +
      (_perf.country ? esc(_perf.country) + ' is at ' : 'Currently at ') +
      '<b>' + _perf.pct + '%</b> of target so far this month' +
      (_perf.through ? ' (sales to ' + esc(_perf.through) + ')' : '') + '.</div>';
  } else if (_perfLoaded) {
    live = '<div style="font-size:12.5px;color:var(--ui-warn);margin-top:6px">Live figure not available yet this month — type a percentage below to see what you would earn.</div>';
  }

  // v130.20 — the two POTS, in dirhams. This is how the bonus has always been
  // explained: on 4,500 a store manager has 450 fixed and 450 variable, and
  // performance moves them.
  var pots = '';
  if (potEach != null) {
    pots =
      '<div style="display:flex;gap:10px;margin-top:14px">' +
        '<div style="flex:1;background:#0B6B34;color:#fff;border-radius:12px;padding:12px;text-align:center">' +
          '<div style="font-size:11px;font-weight:700;letter-spacing:.4px;opacity:.85">FIXED HALF</div>' +
          '<div style="font-size:24px;font-weight:900;margin:2px 0">' + money(potEach) + '</div>' +
          '<div style="font-size:11px;opacity:.85">paid in full at 100%</div>' +
        '</div>' +
        '<div style="flex:1;background:#C6E51A;color:#123456;border-radius:12px;padding:12px;text-align:center">' +
          '<div style="font-size:11px;font-weight:700;letter-spacing:.4px">VARIABLE HALF</div>' +
          '<div style="font-size:24px;font-weight:900;margin:2px 0">' + money(potEach) + '</div>' +
          '<div style="font-size:11px">moves up and down with performance</div>' +
        '</div>' +
      '</div>' +
      '<div style="font-size:11.5px;color:var(--stores-ink-3);margin-top:6px">Your bonus is two halves of ' +
        roleInfo.rate + '% each. At 100% of target you get both in full — ' + money(potEach * 2) + '.</div>';
  }

  var resultBlock = '';
  if (b && salaryNum > 0) {
    resultBlock =
      '<div style="margin-top:14px;padding:14px;border-radius:12px;background:' + (b.earning ? 'var(--elc-green-soft)' : 'var(--danger-bg)') + ';border:1px solid ' + (b.earning ? 'var(--stores-ok-line)' : 'var(--stores-danger-line)') + '">' +
        '<div style="font-size:12.5px;color:var(--text)">At <b>' + displayRate(b.performance) + '%</b> of target you are on track for</div>' +
        '<div style="font-size:32px;font-weight:900;color:' + (b.earning ? 'var(--ok)' : 'var(--danger-deep)') + ';margin:4px 0">' + money(b.money.total) + '</div>' +
        (b.earning
          ? '<div style="font-size:13px;color:var(--text)">' +
              'Variable <b>' + money(b.money.variable) + '</b> of your ' + money(potEach) +
              (b.fixedRate > 0
                ? ' &nbsp;+&nbsp; Fixed <b>' + money(b.money.fixed) + '</b> in full'
                : ' &nbsp;&middot;&nbsp; the fixed ' + money(potEach) + ' unlocks at 100%') +
            '</div>'
          : '<div style="font-size:13px;color:var(--danger-deep)">Below ' + BONUS_THRESHOLD_VARIABLE + '% of target there is no bonus at all.</div>') +
      '</div>';
  } else if (b) {
    resultBlock = '<div style="margin-top:12px;font-size:13px;color:var(--sub)">Enter your fixed salary to see the amounts. ' +
      'At ' + displayRate(b.performance) + '% you would earn <b>' + displayRate(b.totalRate) + '%</b> of it.</div>';
  }

  el.innerHTML = card(
    picker +
    '<div style="display:flex;align-items:center;gap:8px;margin-top:8px">' +
      '<div style="font-size:13px;color:var(--text);flex:1"><span translate="no">' + esc(me.name) + '</span> &middot; <b>' + esc(roleInfo.label) + '</b> &middot; ' + roleInfo.rate + '% + ' + roleInfo.rate + '%</div>' +
      '<button onclick="Bonus.lock()" style="font-size:11.5px;padding:5px 10px;border:1px solid var(--border-strong);background:var(--card);border-radius:8px;cursor:pointer;font-family:inherit">Lock</button>' +
    '</div>' +
    live +

    '<label style="display:block;font-size:12.5px;font-weight:700;margin:14px 0 4px">Your monthly fixed salary</label>' +
    '<input id="bonusSalary" type="text" inputmode="numeric" value="' + esc(salaryVal) + '" placeholder="e.g. 4500" oninput="Bonus.recalc()" ' +
      'style="width:100%;padding:11px;border:1px solid var(--border-strong);border-radius:10px;font-size:16px">' +
    '<div style="font-size:11.5px;color:var(--stores-ink-3);margin-top:5px">Your <b>fixed salary</b> — this includes all your allowances <b>except COLA</b>. ' +
      'This is never saved or sent anywhere: it disappears when you leave this screen.</div>' +

    pots +
    resultBlock +

    '<div style="margin-top:16px;padding-top:12px;border-top:1px solid var(--border)">' +
      '<label style="display:block;font-size:12.5px;font-weight:700;margin-bottom:4px">What if we finish at…</label>' +
      '<div style="display:flex;gap:8px;align-items:center">' +
        '<input type="number" min="0" max="200" step="1" value="' + (_whatIf != null ? _whatIf : '') + '" placeholder="e.g. 120" ' +
          'oninput="Bonus.setWhatIf(this.value)" style="flex:1;padding:10px;border:1px solid var(--border-strong);border-radius:10px;font-size:15px">' +
        '<span style="font-size:15px;font-weight:700">% of target</span>' +
        (_whatIf != null ? '<button onclick="Bonus.clearWhatIf()" style="padding:9px 12px;border:1px solid var(--border-strong);background:var(--card);border-radius:9px;font-size:13px;cursor:pointer">Back to live</button>' : '') +
      '</div>' +
    '</div>' +

    '<div style="margin-top:16px"><div style="font-size:12.5px;font-weight:700;margin-bottom:6px">How it scales for a ' + esc(roleInfo.label.toLowerCase()) + '</div>' +
      ladderHtml(roleInfo.rate, pct, salaryNum) + '</div>' +

    '<div style="margin-top:14px;font-size:11.5px;color:var(--stores-ink-3);line-height:1.5">' +
      'This is an <b>estimate</b> to help you see where you are. The performance figure comes from ' +
      'the sales in Shift and is not the final audited number — your actual bonus is confirmed by Head Office and Finance.' +
    '</div>'
  );

  if (keepSalary) {
    var again = document.getElementById('bonusSalary');
    if (again) { again.focus(); again.setSelectionRange(again.value.length, again.value.length); }
  }
}

export function open() {
  // Re-lock every time the screen is opened: the bonus figures are private, and
  // the till is shared, so the last person's unlock must not carry over.
  _unlocked = false;
  _pinEntry = '';
  _pinMsg = '';
  render();
  loadPerformance().then(function () { render(true); });
}

window.Bonus = {
  open: open, render: render, pickStaff: pickStaff,
  pinKey: pinKey, pinBack: pinBack, lock: lock,
  setWhatIf: setWhatIf, clearWhatIf: clearWhatIf, recalc: recalc,
};
